<template>
  <card class="ppp">
    <div slot="header" class="ppp__header">
      <span class="ppp__header-label">Live preview</span>
      <div class="btn-group btn-group-toggle" data-toggle="buttons">
        <label
          class="btn btn-sm btn-primary btn-simple"
          :class="{ active: frame === 'desktop' }"
        >
          <input type="radio" @click="frame = 'desktop'" :checked="frame === 'desktop'" />
          <i class="tim-icons icon-laptop"></i>
        </label>
        <label
          class="btn btn-sm btn-primary btn-simple"
          :class="{ active: frame === 'mobile' }"
        >
          <input type="radio" @click="frame = 'mobile'" :checked="frame === 'mobile'" />
          <i class="tim-icons icon-mobile"></i>
        </label>
      </div>
    </div>

    <div class="ppp__stage" :class="`ppp__stage--${frame}`">
      <div class="ppp__device" :style="deviceStyle">
        <!-- ─── Two columns on desktop, stacked on mobile ───────────────── -->
        <div class="ppp__layout" :class="`ppp__layout--${frame}`">
          <!-- Summary (sidebar on desktop, top panel on mobile) ─────────── -->
          <div class="ppp__col ppp__col--summary" :class="{ 'ppp__col--summary-mobile': frame === 'mobile' }">
            <div class="ppp__card" :style="cardStyle">
              <div v-if="page.show_logo === 1" class="ppp__sum-head" :style="cardDivider">
                <div v-if="page.logo_image_url" class="ppp__sum-logo">
                  <img :src="page.logo_image_url" alt="Logo" />
                </div>
                <div v-else class="ppp__sum-logo-fallback" :style="logoPlaceholderStyle">
                  {{ logoInitials }}
                </div>
                <div class="ppp__sum-brand" :style="cardTextStyle">Your venue</div>
              </div>

              <div class="ppp__sum-meta" :style="cardSubStyle">
                <div class="ppp__row">
                  <span>Order</span><span class="ppp__num">#A1024</span>
                </div>
                <div class="ppp__row">
                  <span>Amount</span><span class="ppp__num">$120.00</span>
                </div>
              </div>

              <div class="ppp__sum-total" :style="cardDividerTop">
                <div class="ppp__row ppp__row--total" :style="cardTextStyle">
                  <span>Total due</span>
                  <span class="ppp__num">$120.00</span>
                </div>
              </div>

              <div class="ppp__sum-details" :style="cardDividerTop">
                <span class="ppp__sum-toggle" :style="{ color: page.link_color }">
                  View order details ▾
                </span>
              </div>
            </div>
          </div>

          <!-- Action column ──────────────────────────────────────────────── -->
          <div class="ppp__col ppp__col--action">
            <h3 class="ppp__title" :style="titleStyle">
              {{ page.title || 'Complete your payment' }}
            </h3>
            <p v-if="page.subtext" class="ppp__subtext" :style="textStyle">
              {{ page.subtext }}
            </p>

            <!-- Customer info stub ─────────────────────────────────────── -->
            <div class="ppp__group">
              <label :style="textStyle">Your info</label>
              <div class="ppp__input" :style="inputStyle"></div>
              <div class="ppp__input" :style="inputStyle"></div>
            </div>

            <div v-if="activeCustomFields.length > 0" class="ppp__group">
              <div
                v-for="f in activeCustomFields"
                :key="`ppp-cf-${f.id}`"
                class="ppp__field"
              >
                <label :style="textStyle">
                  {{ f.name }}
                  <span v-if="f.required === 1" :style="{ color: page.link_color }">*</span>
                </label>
                <div class="ppp__input" :style="inputStyle"></div>
              </div>
            </div>

            <div v-if="page.allow_tipping === 1" class="ppp__group">
              <label :style="textStyle">{{ page.tip_label || 'Want to leave a tip?' }}</label>
              <div v-if="page.allow_preset_tipping_options === 1" class="ppp__chips">
                <span class="ppp__chip" :style="chipStyle">15%</span>
                <span class="ppp__chip" :style="chipStyle">18%</span>
                <span class="ppp__chip ppp__chip--on" :style="chipActiveStyle">20%</span>
                <span v-if="page.allow_custom_tipping_options === 1" class="ppp__chip" :style="chipStyle">$</span>
              </div>
              <div v-else class="ppp__input ppp__input--sm" :style="inputStyle"></div>
            </div>

            <div v-if="page.show_promo_code_field === 1" class="ppp__group">
              <div class="ppp__input ppp__input--promo" :style="inputStyle">
                <span :style="placeholderStyle">
                  {{ page.promo_code_placeholder_text || 'Promo code' }}
                </span>
              </div>
            </div>

            <div v-if="activePaymentMethods.length > 0" class="ppp__group">
              <label :style="textStyle">Payment method</label>
              <div v-if="page.payment_layout_type === 'dropdown'" class="ppp__input" :style="inputStyle">
                <span :style="cardTextStyle">{{ activePaymentMethods[0].name }}</span>
              </div>
              <div v-else class="ppp__methods">
                <div
                  v-for="(m, i) in activePaymentMethods"
                  :key="`ppp-pm-${m.id}`"
                  class="ppp__method"
                  :style="i === 0 ? methodOnStyle : methodOffStyle"
                >
                  {{ m.name }}
                </div>
              </div>
            </div>

            <!-- Inline CTA (desktop view) -->
            <button v-if="frame === 'desktop'" class="ppp__cta" :style="ctaStyle">
              🔒 Pay $120.00
            </button>

            <div v-if="page.type === 'pos' && page.signature_required === 1" class="ppp__sig">
              <div :style="textStyle" class="ppp__sig-label">Sign here</div>
              <div class="ppp__sig-line"></div>
            </div>
          </div>
        </div>

        <!-- Sticky-feeling CTA bar (mobile view only) -->
        <div v-if="frame === 'mobile'" class="ppp__sticky-cta" :style="stickyBarStyle">
          <button class="ppp__cta" :style="ctaStyle">🔒 Pay $120.00</button>
        </div>
      </div>
    </div>

    <p class="ppp__note">
      Approximate. Final styling renders on the live page.
    </p>
  </card>
</template>

<script>
/**
 * PaymentPagePreview
 * Stylized mockup of the customer-facing checkout. Mirrors the live page's
 * structure: two-column on desktop (action / summary sidebar), stacked on
 * mobile with a top summary panel and a sticky CTA bar.
 *
 * Hardcoded inline styles are intentional — the preview shows what the
 * CUSTOMER will see, which uses the venue's chosen colors, not the admin theme.
 *
 * @prop {Object} page                   the editor's working page object
 * @prop {Array}  linkedCustomFields     fields currently linked to the page
 * @prop {Array}  linkedPaymentMethods   methods currently linked to the page
 */

// System-font fallback appended to the venue's chosen family at render
// time. Must stay in sync with ThemedShell on the customer-app so what
// staff see in the preview matches what customers see live.
const SYSTEM_FONT_STACK = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
function withFontFallback(family) {
  if (!family) return SYSTEM_FONT_STACK;
  if (String(family).includes(',')) return family;
  const safe = /\s/.test(family) && !/^['"]/.test(family) ? `"${family}"` : family;
  return `${safe}, ${SYSTEM_FONT_STACK}`;
}

export default {
  name: 'PaymentPagePreview',
  props: {
    page: { type: Object, required: true },
    linkedCustomFields: { type: Array, default: () => [] },
    linkedPaymentMethods: { type: Array, default: () => [] },
  },
  data() {
    return { frame: 'desktop' };
  },
  computed: {
    activeCustomFields() {
      return this.linkedCustomFields.filter(f => f.active === 1);
    },
    activePaymentMethods() {
      return this.linkedPaymentMethods.filter(m => m.active === 1);
    },
    logoInitials() {
      const n = this.page.name || 'OTC';
      return n.split(/\s+/).map(s => s.charAt(0).toUpperCase()).slice(0, 2).join('');
    },
    pageFontStack() {
      return withFontFallback(this.page.font_family);
    },
    cardFontStack() {
      return withFontFallback(this.page.font_family_card);
    },
    deviceStyle() {
      return {
        background: this.page.background_color,
        fontFamily: this.pageFontStack,
      };
    },
    titleStyle() {
      return { color: this.page.text_color, fontFamily: this.pageFontStack };
    },
    textStyle() {
      return { color: this.page.text_color };
    },
    cardStyle() {
      return {
        background: this.page.card_background_color,
        fontFamily: this.cardFontStack,
      };
    },
    cardTextStyle() { return { color: this.page.card_text_color }; },
    cardSubStyle() {
      return { color: this.page.card_text_color, opacity: 0.65 };
    },
    cardDivider() {
      return { borderBottom: '1px solid ' + this.hexAlpha(this.page.card_text_color, 0.1) };
    },
    cardDividerTop() {
      return { borderTop: '1px solid ' + this.hexAlpha(this.page.card_text_color, 0.1) };
    },
    inputStyle() {
      return {
        background: 'rgba(125,125,125,0.10)',
        borderColor: this.page.input_border_color || 'rgba(125,125,125,0.25)',
        color: this.page.text_color,
      };
    },
    chipStyle() {
      return {
        background: 'rgba(125,125,125,0.10)',
        borderColor: this.page.input_border_color || 'rgba(125,125,125,0.25)',
        color: this.page.text_color,
      };
    },
    chipActiveStyle() {
      return {
        background: this.hexAlpha(this.page.button_color, 0.18),
        borderColor: this.page.button_color,
        color: this.page.button_color,
      };
    },
    methodOnStyle() {
      return {
        background: this.hexAlpha(this.page.button_color, 0.15),
        borderColor: this.page.button_color,
        color: this.page.text_color,
      };
    },
    methodOffStyle() {
      return {
        background: 'rgba(125,125,125,0.06)',
        borderColor: 'rgba(125,125,125,0.22)',
        color: this.page.text_color,
        opacity: 0.7,
      };
    },
    ctaStyle() {
      return {
        background: this.page.button_color,
        color: this.page.button_text_color,
        fontFamily: this.pageFontStack,
      };
    },
    stickyBarStyle() {
      return {
        background: this.page.card_background_color,
        borderTop: '1px solid ' + this.hexAlpha(this.page.card_text_color, 0.1),
      };
    },
    placeholderStyle() {
      return { color: this.page.text_color, opacity: 0.5 };
    },
    logoPlaceholderStyle() {
      return {
        background: this.hexAlpha(this.page.button_color, 0.18),
        color: this.page.button_color,
      };
    },
  },
  methods: {
    hexAlpha(color, alpha) {
      const c = (color || '').trim();
      if (c.startsWith('#')) {
        let hex = c.slice(1);
        if (hex.length === 3) hex = hex.split('').map(h => h + h).join('');
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        if (!isNaN(r)) return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      }
      return c;
    },
  },
};
</script>

<style lang="scss" scoped>
.ppp {
  position: sticky;
  top: 90px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  &__header-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.8);
  }

  &__stage {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
    transition: max-width 0.2s ease;
  }
  &__stage--mobile {
    max-width: 280px;
    margin: 0 auto;
  }

  &__device {
    padding: 18px 14px;
    transition: background 0.25s ease;
    min-height: 360px;
    position: relative;
  }

  // ─── Layout ─────────────────────────────────────────────────────────
  &__layout--desktop {
    display: grid;
    grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
    gap: 12px;
    align-items: start;
  }
  &__layout--mobile {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-bottom: 48px; // breathing room for sticky CTA bar
  }

  // ─── Summary column ─────────────────────────────────────────────────
  &__col--summary {
    // Desktop: appears on the right (placed after action in grid via order).
    order: 2;
  }
  &__col--summary-mobile {
    order: 0; // first on mobile (top panel)
  }
  &__col--action {
    order: 1;
  }

  &__card {
    border-radius: 6px;
    padding: 10px;
    transition: background 0.25s ease, color 0.25s ease;
  }

  &__sum-head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-bottom: 8px;
    margin-bottom: 8px;
  }
  &__sum-logo img {
    max-height: 22px;
    max-width: 60px;
    object-fit: contain;
  }
  &__sum-logo-fallback {
    width: 24px;
    height: 24px;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    font-weight: 700;
    transition: background 0.25s ease, color 0.25s ease;
  }
  &__sum-brand {
    font-size: 10.5px;
    font-weight: 600;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__sum-meta {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 10px;
  }

  &__sum-total {
    margin-top: 8px;
    padding-top: 8px;
  }

  &__sum-details {
    margin-top: 8px;
    padding-top: 8px;
  }
  &__sum-toggle {
    font-size: 10px;
    font-weight: 500;
    cursor: default;
  }

  // ─── Action column ──────────────────────────────────────────────────
  &__title {
    font-size: 15px;
    font-weight: 600;
    margin: 0 0 3px;
    letter-spacing: -0.01em;
    transition: color 0.25s ease;
  }

  &__subtext {
    font-size: 10.5px;
    margin: 0 0 12px;
    opacity: 0.7;
    transition: color 0.25s ease;
  }

  // ─── Shared row / inputs ────────────────────────────────────────────
  &__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 10.5px;
    padding: 1px 0;
  }
  &__row--total { font-weight: 700; font-size: 11.5px; }
  &__num { font-variant-numeric: tabular-nums; }

  &__group {
    margin-bottom: 10px;
    label {
      display: block;
      font-size: 10px;
      margin-bottom: 4px;
      opacity: 0.85;
      transition: color 0.25s ease;
    }
  }

  &__field { margin-bottom: 6px; }

  &__input {
    height: 26px;
    border: 1px solid;
    border-radius: 4px;
    display: flex;
    align-items: center;
    padding: 0 8px;
    font-size: 9.5px;
    margin-bottom: 4px;
    transition: background 0.25s ease, border-color 0.25s ease;
  }
  &__input--sm { height: 24px; }
  &__input--promo { font-style: italic; }

  &__chips { display: flex; gap: 4px; }
  &__chip {
    flex: 1;
    text-align: center;
    padding: 4px;
    border: 1px solid;
    border-radius: 4px;
    font-size: 9.5px;
    font-weight: 500;
    transition: all 0.25s ease;
  }

  &__methods { display: flex; flex-direction: column; gap: 4px; }
  &__method {
    text-align: left;
    padding: 6px 8px;
    border: 1px solid;
    border-radius: 4px;
    font-size: 10px;
    transition: all 0.25s ease;
  }

  &__cta {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    width: 100%;
    padding: 9px;
    border: none;
    border-radius: 6px;
    font-size: 11.5px;
    font-weight: 600;
    margin-top: 4px;
    cursor: default;
    transition: background 0.25s ease, color 0.25s ease;
  }

  &__sticky-cta {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 8px 12px;
    transition: background 0.25s ease, border-color 0.25s ease;
    .ppp__cta {
      margin-top: 0;
    }
  }

  &__sig { margin-top: 10px; font-size: 10px; opacity: 0.8; }
  &__sig-label { margin-bottom: 3px; }
  &__sig-line {
    height: 22px;
    border-bottom: 1px dashed rgba(125, 125, 125, 0.4);
  }

  &__note {
    font-size: 10.5px;
    color: rgba(255, 255, 255, 0.45);
    margin: 10px 0 0;
    text-align: left;
    font-style: italic;
  }
}
</style>

<style>
.white-content .ppp__note { color: rgba(0, 0, 0, 0.45); }
.white-content .ppp__header-label { color: rgba(0, 0, 0, 0.75); }
</style>
